
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

const Security = () => {
  return (
    <div className="container mx-auto py-12 px-4 sm:px-6 lg:px-8 max-w-5xl">
      <div className="mb-8 text-center">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 via-blue-500 to-cyan-400 bg-clip-text text-transparent mb-4">
          Security
        </h1>
        <p className="text-gray-500 text-lg max-w-3xl mx-auto">
          Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        </p>
        <Separator className="my-8" />
      </div>

      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">Our Security Commitment</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <p>
            At DGK Freelance Hub, security is at the core of everything we do. We understand that your data is valuable, and we are committed to implementing the highest standards of security to protect your information and transactions on our platform.
          </p>
          <p>
            Our comprehensive security approach encompasses infrastructure security, application security, data protection, and operational security practices to provide you with a secure environment for your freelancing activities.
          </p>
        </CardContent>
      </Card>

      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">Infrastructure Security</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <h3 className="text-xl font-semibold text-gray-700">Secure Hosting</h3>
          <p>
            Our platform is hosted on enterprise-grade cloud infrastructure with multiple layers of security controls, including physical security, network security, and system-level protections.
          </p>
          
          <h3 className="text-xl font-semibold text-gray-700 mt-6">Network Protection</h3>
          <p>
            We implement advanced firewalls, intrusion detection and prevention systems, and regular network security scans to protect against unauthorized access and cyber threats.
          </p>
          
          <h3 className="text-xl font-semibold text-gray-700 mt-6">DDoS Mitigation</h3>
          <p>
            Our infrastructure includes built-in protection against Distributed Denial of Service (DDoS) attacks to ensure platform availability and performance even during attack attempts.
          </p>
        </CardContent>
      </Card>

      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">Data Security</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <h3 className="text-xl font-semibold text-gray-700">Encryption</h3>
          <p>
            All sensitive data is encrypted both in transit and at rest using industry-standard encryption protocols. We use TLS 1.3 for data in transit and AES-256 encryption for data at rest.
          </p>
          
          <h3 className="text-xl font-semibold text-gray-700 mt-6">Secure Data Storage</h3>
          <p>
            Your data is stored in secure, redundant systems with strict access controls. We implement data segregation practices to ensure proper isolation of customer data.
          </p>
          
          <h3 className="text-xl font-semibold text-gray-700 mt-6">Backup and Recovery</h3>
          <p>
            We maintain regular, encrypted backups of all critical data with tested recovery procedures to ensure data durability and business continuity.
          </p>
        </CardContent>
      </Card>

      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">Application Security</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <h3 className="text-xl font-semibold text-gray-700">Secure Development</h3>
          <p>
            We follow secure coding practices and conduct regular code reviews to identify and remediate security vulnerabilities throughout the development lifecycle.
          </p>
          
          <h3 className="text-xl font-semibold text-gray-700 mt-6">Vulnerability Management</h3>
          <p>
            Our security team conducts regular vulnerability assessments and penetration testing to identify and address potential security issues before they can be exploited.
          </p>
          
          <h3 className="text-xl font-semibold text-gray-700 mt-6">Third-Party Security Audits</h3>
          <p>
            We engage independent security firms to conduct regular security assessments of our platform to validate our security controls and identify areas for improvement.
          </p>
        </CardContent>
      </Card>

      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">Account Security</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <h3 className="text-xl font-semibold text-gray-700">Multi-Factor Authentication</h3>
          <p>
            We provide multi-factor authentication options to add an additional layer of security to your account, preventing unauthorized access even if your password is compromised.
          </p>
          
          <h3 className="text-xl font-semibold text-gray-700 mt-6">Password Security</h3>
          <p>
            Passwords are securely hashed using strong, industry-standard algorithms. We enforce password complexity requirements and provide secure password reset mechanisms.
          </p>
          
          <h3 className="text-xl font-semibold text-gray-700 mt-6">Session Management</h3>
          <p>
            We implement secure session handling with automatic timeout features and the ability to view and terminate active sessions from your account settings.
          </p>
        </CardContent>
      </Card>

      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">Payment Security</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <h3 className="text-xl font-semibold text-gray-700">PCI DSS Compliance</h3>
          <p>
            Our payment processing is PCI DSS compliant, ensuring that all payment data is handled according to the strictest industry standards for credit card processing.
          </p>
          
          <h3 className="text-xl font-semibold text-gray-700 mt-6">Secure Payment Partners</h3>
          <p>
            We partner with industry-leading payment processors to ensure secure handling of financial transactions. We do not store complete credit card information on our servers.
          </p>
          
          <h3 className="text-xl font-semibold text-gray-700 mt-6">Blockchain Security</h3>
          <p>
            For cryptocurrency transactions, we implement advanced blockchain security measures, including multi-signature wallets and secure key management practices.
          </p>
        </CardContent>
      </Card>

      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">Operational Security</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <h3 className="text-xl font-semibold text-gray-700">Access Controls</h3>
          <p>
            We implement strict access controls based on the principle of least privilege. Our staff only have access to the systems and data necessary for their roles.
          </p>
          
          <h3 className="text-xl font-semibold text-gray-700 mt-6">Security Monitoring</h3>
          <p>
            We maintain comprehensive security monitoring and alerting systems to detect and respond to suspicious activities in real-time.
          </p>
          
          <h3 className="text-xl font-semibold text-gray-700 mt-6">Incident Response</h3>
          <p>
            We have a formal incident response plan with defined procedures for addressing security events swiftly and effectively to minimize potential impact.
          </p>
        </CardContent>
      </Card>

      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">Security Recommendations</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <p>
            While we implement robust security measures on our platform, we recommend these additional steps to enhance your account security:
          </p>
          <ul className="list-disc pl-6 space-y-2 mt-2">
            <li>Enable multi-factor authentication for your account</li>
            <li>Use a unique, strong password for DGK Freelance Hub</li>
            <li>Keep your operating system and browsers updated</li>
            <li>Be cautious of phishing attempts and verify email communications</li>
            <li>Regularly monitor your account activity</li>
            <li>Log out of your account when using shared devices</li>
          </ul>
        </CardContent>
      </Card>

      <Card className="mb-8 shadow-lg border-0 bg-white/50 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-gray-800">Contact Us</CardTitle>
        </CardHeader>
        <CardContent className="text-gray-600 space-y-4">
          <p>
            If you have any questions about our security practices or would like to report a security concern, please contact our security team at:
          </p>
          <p className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-100 mt-2">
            <strong>Email:</strong> security@dgkfreelancehub.com<br />
            <strong>Address:</strong> 123 Blockchain Boulevard, Crypto City, CC 12345<br />
            <strong>Phone:</strong> +1 (555) 123-4567
          </p>
        </CardContent>
      </Card>

      <div className="text-center mt-12 text-sm text-gray-500">
        <p>© {new Date().getFullYear()} DGK Freelance Hub. All rights reserved.</p>
      </div>
    </div>
  );
};

export default Security;
